import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Gift, Sparkles, Crown } from "lucide-react";
import { api } from "@/App";
import { GoldenRingAvatar } from "./GoldenRingAvatar";
import { AdeLoadingSpinner } from "./AdeLoadingSpinner";
import { AdeLogoIcon } from "./AdeIcon";

// Format coins helper
const formatCoins = (amount) => {
  if (amount >= 1000000) {
    const m = amount / 1000000;
    return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const k = amount / 1000;
    return k % 1 === 0 ? `${k.toFixed(0)}K` : `${k.toFixed(1)}K`;
  }
  return amount.toString();
};

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return "ora";
  if (diffMins < 60) return `${diffMins}m fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  if (diffDays < 7) return `${diffDays}g fa`;
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};

// ===== LIKES LIST MODAL =====
export const LikesListModal = ({ isOpen, onClose, photoId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  useEffect(() => {
    if (isOpen && photoId) {
      fetchLikes();
    }
  }, [isOpen, photoId]);
  
  const fetchLikes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/photos/${photoId}/likes`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching likes:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUserClick = (userId) => {
    onClose();
    navigate(`/app/user/${userId}`);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto bg-white p-0 overflow-hidden max-h-[80vh]">
        <DialogHeader className="p-4 pb-2 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-3 text-lg">
            {/* Logo ADE grande (60px width) con animazione sparkle */}
            <AdeLogoIcon size={60} showSparkle={true} />
            <span className="font-bold text-gray-800 text-xl">Ade Like</span>
            <span className="text-sm text-gray-500 font-normal">({users.length})</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Lista utenti che hanno messo Ade Like a questa foto
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <AdeLoadingSpinner size="md" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <AdeLogoIcon size={72} grayscale={true} showSparkle={false} />
              <p className="text-sm mt-2">Nessun Ade Like ancora</p>
            </div>
          ) : (
            <div className="divide-y">
              {users.map((user, index) => (
                <button
                  key={user.user_id || index}
                  onClick={() => handleUserClick(user.user_id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                  data-testid={`like-user-${user.user_id}`}
                >
                  {user.golden?.level > 0 ? (
                    <GoldenRingAvatar
                      src={user.picture || "/images/default-avatar.png"}
                      name={user.name}
                      size="sm"
                      level={user.golden.level}
                    />
                  ) : (
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.picture || "/images/default-avatar.png"} />
                      <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{user.name}</p>
                    {user.username && (
                      <p className="text-xs text-gray-500 truncate">@{user.username}</p>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-400">
                    {formatTimeAgo(user.liked_at)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== GIFTS LIST MODAL =====
// Shows grouped summary: "Cuori 3x" instead of individual gifts
export const GiftsListModal = ({ isOpen, onClose, photoId }) => {
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [groupedGifts, setGroupedGifts] = useState([]);
  const [isPrivate, setIsPrivate] = useState(false);
  
  useEffect(() => {
    if (isOpen && photoId) {
      fetchGifts();
    }
  }, [isOpen, photoId]);
  
  const fetchGifts = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/gifts/post/${photoId}`);
      
      // Privacy check — owner disabled gift visibility
      if (response.data.visible === false) {
        setIsPrivate(true);
        setGifts([]);
        setTotalValue(0);
        setGroupedGifts([]);
        setLoading(false);
        return;
      }
      setIsPrivate(false);
      
      const giftsData = response.data.gifts || [];
      setGifts(giftsData);
      setTotalValue(response.data.total_value || 0);
      
      // Use pre-grouped data from backend if available, else group locally
      if (response.data.by_type && response.data.by_type.length > 0) {
        setGroupedGifts(response.data.by_type.map(g => ({
          gift_type: g.gift_type,
          gift_name: g.name,
          gift_image: g.gift_image || `/images/gifts/${g.gift_type}.png`,
          count: g.count,
          total_value: g.total_value || 0,
        })));
      } else {
        // Fallback: group locally
        const grouped = {};
        giftsData.forEach(gift => {
          const key = gift.gift_type || gift.gift_name;
          if (!grouped[key]) {
            grouped[key] = {
              gift_type: gift.gift_type,
              gift_name: gift.gift_name,
              gift_image: gift.gift_image || `/images/gifts/${gift.gift_type}.png`,
              cost: gift.cost,
              count: 0,
              total_value: 0
            };
          }
          grouped[key].count += 1;
          grouped[key].total_value += gift.cost;
        });
        setGroupedGifts(Object.values(grouped).sort((a, b) => b.total_value - a.total_value));
      }
    } catch (error) {
      console.error('Error fetching gifts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto bg-white p-0 overflow-hidden max-h-[80vh]">
        <DialogHeader className="p-4 pb-2 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Gift className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-800">Regali Ricevuti</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Riepilogo dei regali ricevuti su questa foto
          </DialogDescription>
          
          {/* Total value banner */}
          {totalValue > 0 && (
            <div className="mt-2 flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
              <img 
                src="/images/ade-coin.jpeg" 
                alt="ADE" 
                className="w-8 h-8 rounded-full object-cover bg-white dark:bg-black"
              />
              <span className="text-base font-bold text-amber-700">
                Totale: {formatCoins(totalValue)} ADE Coins
              </span>
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
          )}
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[60vh] p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <AdeLoadingSpinner size="md" />
            </div>
          ) : isPrivate ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Gift className="w-12 h-12 mb-2 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">Regali nascosti</p>
              <p className="text-xs text-gray-400 mt-1">{"L'utente ha scelto di non mostrare i regali"}</p>
            </div>
          ) : groupedGifts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Gift className="w-12 h-12 mb-2" />
              <p className="text-sm">Nessun regalo ancora</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {groupedGifts.map((giftGroup, index) => (
                <div
                  key={giftGroup.gift_type || index}
                  className="flex flex-col items-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100"
                >
                  {/* Gift image */}
                  <div className="relative mb-2">
                    <img 
                      src={giftGroup.gift_image}
                      alt={giftGroup.gift_name}
                      className="w-16 h-16 object-contain drop-shadow-lg"
                    />
                    {giftGroup.cost >= 100000 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center shadow">
                        <Crown className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Gift name and count */}
                  <p className="font-bold text-gray-800 text-center">{giftGroup.gift_name}</p>
                  <p className="text-2xl font-extrabold text-amber-600">{giftGroup.count}x</p>
                  
                  {/* Total value for this gift type */}
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                    <img 
                      src="/images/ade-coin.jpeg" 
                      alt="ADE" 
                      className="w-5 h-5 rounded-full object-cover bg-white dark:bg-black"
                    />
                    <span className="font-bold">{formatCoins(giftGroup.total_value)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default { LikesListModal, GiftsListModal };
